<template>
  <div>
    <div v-if="isLoggedIn && isPremium">

      <h1 class="mb-3">Your Enquiries</h1>

      <b-row class="match-height">
        <b-col cols="12">
          <b-card class="enquiry-history-card-with-hint">
            <hint-popup
              :id="11"
              class="enquiry-history-hint-popup"
            />
            <h2 class="mb-2 enquiry-history-title">Enquiry History</h2>

            <div v-if="enquiries.loading" class="d-flex justify-content-center py-3">
              <b-spinner variant="primary" />
            </div>

            <div v-else-if="enquiries.error" class="text-center py-3">
              <p class="text-danger">Failed to load enquiries</p>
            </div>

            <div v-else-if="enquiries.data.length === 0" class="text-center py-3">
              <p class="text-muted">No enquiries sent yet</p>
            </div>

            <div v-else>
              <b-table
                :items="enquiries.data"
                :fields="enquiryFields"
                responsive
                striped
                hover
              >
                <template #cell(datereceived)="data">
                  {{ data.value }}
                </template>
                <template #cell(provider)="data">
                  {{ data.value }}
                </template>
                <template #cell(reporttitle)="data">
                  {{ data.value }}
                </template>
                <template #cell(message)="data">
                  <div style="max-width: 400px; white-space: pre-wrap;">
                    {{ data.value }}
                  </div>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>

        <!-- Provider enquiry forms are temporarily hidden. -->
        <b-col v-if="false" cols="12" md="4">
          <b-card title="Send Enquiry" class="h-100">
            <b-card-text>
              Contact a provider directly about a product.
            </b-card-text>
            <b-form @submit.prevent="submitEnquiry">
              <b-form-group label="Provider">
                <b-form-select
                  v-model="enquiryForm.selectedProvider"
                  :options="providerOptions"
                  @change="onProviderChange"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select a provider --
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
              <b-form-group label="Product">
                <b-form-select
                  v-model="enquiryForm.selectedProduct"
                  :options="productOptions"
                  :disabled="!enquiryForm.selectedProvider || productOptions.length === 0"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      {{ enquiryForm.selectedProvider ? '-- Select a product --' : '-- Select a provider first --' }}
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
              <b-form-group label="Name">
                <b-form-input v-model="enquiryForm.name" :readonly="userDataLoaded" />
              </b-form-group>
              <b-form-group label="Company">
                <b-form-input v-model="enquiryForm.company" :readonly="userDataLoaded" />
              </b-form-group>
              <b-form-group label="Email Address">
                <b-form-input v-model="enquiryForm.email" type="email" :readonly="userDataLoaded" />
              </b-form-group>
              <b-form-group label="Message">
                <b-form-textarea v-model="enquiryForm.message" rows="3" placeholder="Enter your message here..." />
              </b-form-group>
              <div class="text-right">
                <b-button variant="primary" type="submit" :disabled="formLoading">
                  <b-spinner v-if="formLoading" small />
                  <span v-else>Submit</span>
                </b-button>
              </div>
            </b-form>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <UpgradeToPremium />
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BTable,
  BSpinner,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BButton,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import UpgradeToPremium from '@/views/components/UpgradeToPremium.vue'
import HintPopup from '@/views/components/HintPopup.vue'

export default {
  name: 'YourEnquiries',
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BTable,
    BSpinner,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BButton,
    UpgradeToPremium,
    HintPopup,
  },
  data() {
    return {
      enquiries: {
        data: [],
        loading: false,
        error: false,
      },
      enquiryFields: [
        { key: 'datereceived', label: 'Date Received', sortable: true },
        { key: 'provider', label: 'Provider', sortable: true },
        { key: 'reporttitle', label: 'Report Title', sortable: true },
        { key: 'message', label: 'Message', sortable: false },
      ],
      providerOptions: [],
      productOptions: [],
      enquiryForm: {
        selectedProvider: null,
        selectedProduct: null,
        name: '',
        company: '',
        email: '',
        message: '',
      },
      userDataLoaded: false,
      formLoading: false,
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'isPremium',
    ]),
  },
  created() {
    this.getAllProviderEnquiries()
  },
  activated() {
    this.getAllProviderEnquiries()
  },
  methods: {
    getAllProviderEnquiries() {
      this.enquiries.loading = true
      this.enquiries.error = false

      this.$http.get('premium.cfc', {
        params: {
          method: 'getAllProviderEnquiries',
        },
      })
        .then(response => {
          console.log('All Provider Enquiries:', response.data)
          if (response.data.success && response.data.enquiries) {
            this.enquiries.data = response.data.enquiries
          }
        })
        .catch(error => {
          console.error('Error loading all provider enquiries:', error)
          this.enquiries.error = true
        })
        .finally(() => {
          this.enquiries.loading = false
        })
    },
    loadProviders() {
      this.$http.get('public.cfc?method=getCategorySummary&category=provider')
        .then(response => {
          const providers = response.data.CONTENT || []
          this.providerOptions = providers.map(provider => ({
            value: provider.id,
            text: provider.name,
            email: provider.email || '',
          }))
        })
        .catch(error => {
          console.error('Error loading providers:', error)
        })
    },
    onProviderChange(providerId) {
      this.enquiryForm.selectedProduct = null
      this.productOptions = []

      if (!providerId) return

      const selectedProvider = this.providerOptions.find(p => p.value === providerId)
      if (!selectedProvider) return

      this.loadProviderProducts(selectedProvider.text)
    },
    loadProviderProducts(providerName) {
      const filter = {
        search: '',
        filter: {
          providers: [providerName],
        },
        constraints: {
          dateFrom: '',
          dateTo: '',
          dateType: 'None',
        },
        sortBy: '',
        sortDesc: false,
      }

      this.$http.get('lifecycle/public/data.cfc?method=getFilterResults', {
        params: {
          searchFilter: filter,
          perpage: 1000,
          page: 1,
        },
      })
        .then(response => {
          const products = response.data.DATA || []
          const sortedProducts = products.sort((a, b) => b.productid - a.productid)

          this.productOptions = sortedProducts.map(product => ({
            value: product.productid,
            text: `${product.productid} ${product.productname}`,
            productid: product.productid,
            productname: product.productname,
          }))
        })
        .catch(error => {
          console.error('Error loading provider products:', error)
        })
    },
    loadUserData() {
      this.$http
        .get('account.cfc', {
          params: {
            method: 'getUserLogin',
          },
        })
        .then(response => {
          if (response.data.success && response.data.data) {
            this.enquiryForm.name = response.data.data.fullName || ''
            this.enquiryForm.company = response.data.data.companyName || ''
            this.enquiryForm.email = response.data.data.email || ''
            this.userDataLoaded = true
          }
        })
        .catch(error => {
          console.error('Error loading user data:', error)
        })
    },
    submitEnquiry() {
      if (!this.enquiryForm.selectedProvider) {
        this.$bvToast.toast('Please select a provider.', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (!this.enquiryForm.selectedProduct) {
        this.$bvToast.toast('Please select a product.', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (this.enquiryForm.message.length < 10) {
        this.$bvToast.toast('Message is too short. Please enter at least 10 characters.', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.formLoading = true

      const selectedProvider = this.providerOptions.find(
        p => p.value === this.enquiryForm.selectedProvider,
      )
      const selectedProduct = this.productOptions.find(
        p => p.value === this.enquiryForm.selectedProduct,
      )

      if (!selectedProvider || !selectedProduct) {
        this.$bvToast.toast('Selected provider or product not found. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        this.formLoading = false
        return
      }

      const submittedForm = {
        reportnumber: selectedProduct.productid,
        provideremail: selectedProvider.email,
        productname: selectedProduct.productname,
        name: this.enquiryForm.name,
        company: this.enquiryForm.company,
        email: this.enquiryForm.email,
        message: this.enquiryForm.message,
        ProviderId: this.enquiryForm.selectedProvider,
      }

      this.$http
        .get('research.cfc', {
          params: {
            method: 'sendProviderEnquiry',
            submittedForm,
          },
        })
        .then(response => {
          this.formLoading = false
          if (response.data.success) {
            this.enquiryForm.message = ''
            this.enquiryForm.selectedProduct = null
            this.enquiryForm.selectedProvider = null
            this.productOptions = []
            this.$bvToast.toast('Your enquiry has been sent to the provider', {
              title: 'Success',
              variant: 'success',
              solid: true,
            })
            this.getAllProviderEnquiries()
          } else {
            this.$bvToast.toast('Your enquiry could not be sent. Please try again', {
              title: 'Failed',
              variant: 'danger',
              solid: true,
            })
          }
        })
        .catch(error => {
          console.error('Error submitting enquiry:', error)
          this.formLoading = false
          this.$bvToast.toast('Your enquiry could not be sent. Please try again', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        })
    },
  },
}
</script>

<style scoped>
.enquiry-history-card-with-hint ::v-deep .card-body {
  position: relative;
}

.enquiry-history-hint-popup {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}

.enquiry-history-title {
  padding-right: 2.5rem;
}
</style>
